import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import {useLanguage} from '../context/LanguageContext';
import {FaUsers,FaShieldAlt,FaBrain,FaChalkboardTeacher,FaCheckCircle,FaArrowRight} from 'react-icons/fa';

const Schools=()=> {
  const {language}=useLanguage();

  const content={
    en: {
      title: "COSMO Schools",
      subtitle: "Supporting Schools to Promote Mental Wellbeing and Academic Success",
      intro: "COSMO offers a comprehensive system designed to foster mental wellbeing, improve classroom dynamics, and support both students and teachers. By integrating innovative assessment tools such as the Picture Frustration Study using the COSMO Scoring Method, our platform provides schools with concrete data and actionable strategies to create balanced, inclusive, and stimulating learning environments.",
      sections: [
        {
          id: 'balance',
          icon: FaUsers,
          title: "Structuring Balanced Classrooms from the Start",
          text: "At the beginning of the school year, COSMO, where possible, provides tools to analyze selection interviews, offering valuable insights into students' relational tendencies. These elements make it possible to build well-balanced classes, reducing potential conflicts and fostering a cooperative and inclusive atmosphere.",
          subtext: "Thanks to the COSMO Scoring Method, schools can anticipate how students react to frustrating situations and adopt targeted strategies to promote harmony and engagement from the very first days of school."
        },
        {
          id: 'risks',
          icon: FaShieldAlt,
          title: "Early Detection of Risks Throughout the Year",
          text: "As the months go by, COSMO plays a key role in the early detection of risk factors. Our data-driven assessment tools help identify at-risk students, including:",
          bullets: [
            "Students vulnerable to bullying",
            "Students at risk of engaging in aggressive behavior",
            "Students in need of emotional support"
          ],
          subtext: "This personalized approach allows schools to go beyond standard generic preventive measures, designing interventions that respond to the specific needs of each student and group. With COSMO, schools can proactively address challenges, promoting resilience and reducing the risk of negative outcomes."
        },
        {
          id: 'engagement',
          icon: FaBrain,
          title: "Actively Engaging Students in Personal Growth",
          text: "One of COSMO's most innovative aspects is its ability to actively engage students in their own development. By using tools such as the Picture Frustration Study, students learn to independently code their responses, gaining insight into their emotional and relational patterns. This process:",
          bullets: [
            "Increases self-awareness",
            "Promotes emotional regulation and resilience",
            "Strengthens the ability to manage complex social interactions"
          ],
          subtext: "These activities are easy to implement and help improve group cohesion, creating a sense of belonging and a more positive environment for all students."
        },
        {
          id: 'teachers',
          icon: FaChalkboardTeacher,
          title: "Concrete Data for Teachers",
          text: "COSMO provides precise and actionable data to optimize teaching strategies, group management, and personalized interventions.",
          subtext: "Whether it is supporting students with specific difficulties, improving relational dynamics, or fostering personal growth, COSMO provides the tools and information needed to make a real difference in students' lives."
        }
      ],
      transformTitle: "Transforming Schools into Inclusive and Supportive Learning Environments",
      transformText1: "With COSMO, schools can go beyond traditional academic parameters, recognizing emotional wellbeing as a key element for student success. Our system ensures that school is not only a place of learning, but also an environment where students feel supported, understood, and motivated to grow.",
      transformThrough: "Through:",
      transformBullets: [
        "Targeted assessments",
        "Active student engagement",
        "Personalized interventions"
      ],
      transformConclusion: "COSMO transforms the classroom into a space where every student can grow, both academically and emotionally.",
      ctaTitle: "Join Us to Build a Better Future",
      ctaText: "Discover how COSMO can make a difference in your school. Together, we can create better learning environments and build a better future for all students.",
      ctaButton: "Contact Our Team"
    },
    it: {
      title: "COSMO Scuole",
      subtitle: "Supportare le Scuole per Promuovere il Benessere Mentale e il Successo Scolastico",
      intro: "COSMO offre un sistema completo progettato per favorire il benessere mentale,migliorare le dinamiche di classe e supportare studenti ed insegnanti. Integrando strumenti di valutazione innovativi come il Picture Frustration Study secondo il Metodo di Scoring di COSMO,la nostra piattaforma fornisce alle scuole dati concreti e strategie attuabili per creare ambienti di apprendimento equilibrati,inclusivi e stimolanti.",
      sections: [
        {
          id: 'balance',
          icon: FaUsers,
          title: "Strutturare Classi Equilibrate sin dall'Inizio",
          text: "All'inizio dell'anno scolastico,COSMO,ove possibile mette a disposizione strumenti per analizzare i colloqui di selezione,offrendo informazioni preziose sulle tendenze relazionali degli studenti. Questi elementi permettono di costruire classi ben bilanciate,riducendo i potenziali conflitti e favorendo un'atmosfera cooperativa e inclusiva.",
          subtext: "Grazie al Metodo di Scoring di COSMO,le scuole possono comprendere in anticipo come gli studenti reagiscono alle situazioni frustranti e adottare strategie mirate per promuovere l'armonia e il coinvolgimento degli stessi sin dai primi giorni di scuola."
        },
        {
          id: 'risks',
          icon: FaShieldAlt,
          title: "Rilevazione Precoce dei Rischi Durante l'Anno",
          text: "Con il passare dei mesi,COSMO assume un ruolo chiave nella rilevazione precoce dei fattori di rischio. I nostri strumenti di valutazione basati sui dati aiutano a identificare gli studenti a rischio,inclusi:",
          bullets: [
            "Studenti vulnerabili al bullismo",
            "Studenti a rischio di assumere comportamenti aggressivi",
            "Studenti con bisogno di supporto emotivo"
          ],
          subtext: "Questo approccio personalizzato consente alle scuole di andare oltre le usuali misure preventive generiche,progettando interventi che rispondano alle necessità specifiche di ogni studente e gruppo. Con COSMO,le scuole possono affrontare proattivamente le difficoltà,promuovendo la resilienza e riducendo il rischio di esiti negativi."
        },
        {
          id: 'engagement',
          icon: FaBrain,
          title: "Coinvolgere Attivamente gli Studenti nella Crescita Personale",
          text: "Uno degli aspetti più innovativi di COSMO è la sua capacità di coinvolgere attivamente gli studenti nel proprio sviluppo. Utilizzando strumenti come il Picture Frustration Study,gli studenti imparano a codificare autonomamente le proprie risposte,acquisendo comprensione dei propri schemi emotivi e relazionali. Questo processo:",
          bullets: [
            "Aumenta la consapevolezza di sé",
            "Favorisce la regolazione emotiva e la resilienza",
            "Rafforza la capacità di gestire le interazioni sociali complesse"
          ],
          subtext: "Queste attività sono facili da implementare e aiutano a migliorare la coesione di gruppo,creando un senso di appartenenza e un ambiente più positivo per tutti gli studenti."
        },
        {
          id: 'teachers',
          icon: FaChalkboardTeacher,
          title: "Dati Concreti per gli Insegnanti",
          text: "COSMO fornisce dati precisi e utilizzabili per ottimizzare le strategie didattiche,la gestione dei gruppi e gli interventi personalizzati.",
          subtext: "Che si tratti di supportare studenti con difficoltà specifiche,migliorare le dinamiche relazionali o favorire la crescita personale,COSMO mette a disposizione gli strumenti e le informazioni necessarie per fare una reale differenza nella vita degli studenti."
        }
      ],
      transformTitle: "Trasformare le Scuole in Ambienti di Apprendimento Inclusivi e Supportivi",
      transformText1: "Con COSMO,le scuole possono andare oltre i tradizionali parametri accademici,riconoscendo il benessere emotivo come elemento chiave per il successo degli studenti. Il nostro sistema assicura che la scuola non sia solo un luogo di apprendimento,ma anche un ambiente in cui gli studenti si sentano supportati,compresi e motivati a crescere.",
      transformThrough: "Attraverso:",
      transformBullets: [
        "Valutazioni mirate",
        "Coinvolgimento attivo degli studenti",
        "Interventi personalizzati"
      ],
      transformConclusion: "COSMO trasforma la classe in uno spazio in cui ogni studente può crescere,sia accademicamente che emotivamente.",
      ctaTitle: "Unisciti a Noi per Costruire un Futuro Migliore",
      ctaText: "Scopri come COSMO può fare la differenza nella tua scuola. Insieme,possiamo creare ambienti di apprendimento migliori e costruire un futuro migliore per tutti gli studenti.",
      ctaButton: "Contatta il Nostro Team"
    }
  };

  const currentContent=content[language] || content.en;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-orange-600 text-white py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/97cec6254316a951f824fbca4347b65f7f1a7e8bfb3213050d69d6b9d996aba3dd0371de97ad8eb7d82529d579c85e3f/images/aW1hZ2UvcG5n/8285e8ce6e307e563b30eed6e0f0d2d3.png" 
            alt="School Background" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <motion.h1 
            initial={{opacity: 0,y: 20}}
            animate={{opacity: 1,y: 0}}
            transition={{duration: 0.5}}
            className="text-4xl md:text-5xl font-extrabold tracking-tight mb-6"
          >
            {currentContent.title}
          </motion.h1>
          <motion.p 
            initial={{opacity: 0,y: 20}}
            animate={{opacity: 1,y: 0}}
            transition={{delay: 0.2,duration: 0.5}}
            className="text-xl md:text-2xl max-w-3xl mx-auto font-medium text-orange-100"
          >
            {currentContent.subtitle}
          </motion.p>
        </div>
      </div>

      {/* Intro Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10 relative z-20">
        <motion.div 
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          transition={{delay: 0.4,duration: 0.5}}
          className="bg-white rounded-xl shadow-xl p-8 md:p-10 text-center border border-gray-100"
        >
          <p className="text-lg text-gray-700 leading-relaxed">
            {currentContent.intro}
          </p>
        </motion.div>
      </div>

      {/* Main Features Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {currentContent.sections.map((section,index)=> (
            <motion.div
              key={section.id}
              initial={{opacity: 0,y: 20}}
              whileInView={{opacity: 1,y: 0}}
              viewport={{once: true,margin: "-50px"}}
              transition={{delay: index * 0.1,duration: 0.5}}
              className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow"
            >
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                <section.icon className="text-2xl text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{section.title}</h3>
              <p className="text-gray-600 mb-4 leading-relaxed">{section.text}</p>
              
              {section.bullets && (
                <ul className="space-y-2 mb-4">
                  {section.bullets.map((bullet,i)=> (
                    <li key={i} className="flex items-start">
                      <FaCheckCircle className="text-green-500 mt-1 mr-3 flex-shrink-0" />
                      <span className="text-gray-700 font-medium">{bullet}</span>
                    </li>
                  ))}
                </ul>
              )}
              
              <p className="text-gray-600 leading-relaxed bg-gray-50 p-4 rounded-lg border-l-4 border-orange-400">
                {section.subtext}
              </p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Transformation Section */}
      <div className="bg-gradient-to-br from-indigo-900 to-gray-900 text-white py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{opacity: 0,y: 20}}
            whileInView={{opacity: 1,y: 0}}
            viewport={{once: true}}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-orange-400">
              {currentContent.transformTitle}
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              {currentContent.transformText1}
            </p>
            
            <div className="bg-white/10 backdrop-blur-md rounded-xl p-8 inline-block text-left mb-8">
              <p className="text-lg font-semibold mb-4 text-white">{currentContent.transformThrough}</p>
              <ul className="space-y-3">
                {currentContent.transformBullets.map((bullet,i)=> (
                  <li key={i} className="flex items-center text-lg text-gray-200">
                    <FaCheckCircle className="text-orange-500 mr-3" />
                    {bullet}
                  </li>
                ))}
              </ul>
            </div>
            
            <p className="text-2xl font-medium text-white italic">
              "{currentContent.transformConclusion}"
            </p>
          </motion.div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-white text-center">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{currentContent.ctaTitle}</h2>
          <p className="text-xl text-gray-600 mb-10">{currentContent.ctaText}</p>
          <Link 
            to="/contact" 
            className="inline-flex items-center px-8 py-4 bg-orange-600 text-white rounded-full font-bold text-lg hover:bg-orange-700 transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            {currentContent.ctaButton}
            <FaArrowRight className="ml-3" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Schools;